"use client";

import type { ComponentPropsWithoutRef } from "react";
import { clsx } from "clsx";
import { Button } from "@jetblue/kit/button";
import { twMerge } from "tailwind-merge";
import { CloseButton } from "@jetblue/kit/close-button";
import { useSmartBanner } from "./use-smart-banner";
import NextImage from "next/image";
import { DEFAULT_LABELS, type SmartBannerLabels } from "./labels";

/**
 * Data stored when the banner is dismissed
 */
export type SmartBannerDismissalData = {
  dismissedAt: string;
};
export type SmartBannerState = {
  isVisible: boolean;
  isDismissed: boolean;
  daysUntilNextShow: number;
};
export type SmartBannerProperties = ComponentPropsWithoutRef<"div"> & {
  /**
   * The app store URL where users can download the app
   */
  appStoreUrl: string;

  /**
   * Optional deep link URL to open the app directly if installed
   */
  appDeepLink?: string | undefined;

  /**
   * The name of the app to display
   * @default "JetBlue"
   */
  appName?: string;

  /**
   * A brief description of what the app offers
   * @default "Book & manage trips"
   */
  appDescription?: string;

  /**
   * URL to the app icon/logo
   */
  appIconUrl?: string;

  /**
   * Labels for the smart banner text content
   */
  labels?: Partial<SmartBannerLabels>;

  /**
   * Number of days to wait before showing the banner again after dismissal
   */
  dismissalDurationDays?: number;

  /**
   * Whether to show the banner automatically
   */
  isEnabled?: boolean;

  /**
   * Additional class names for the banner container
   */
  className?: string;
};
export const SmartBanner = ({
  appStoreUrl,
  appDeepLink,
  appIconUrl,
  labels,
  dismissalDurationDays,
  isEnabled,
  className = "",
  ...properties
}: SmartBannerProperties) => {
  const mergedLabels = { ...DEFAULT_LABELS, ...labels };

  const { isVisible, dismissBanner, openApp } = useSmartBanner({
    appDeepLink,
    appStoreUrl,
    dismissalDurationDays,
    isEnabled,
  });

  if (!isVisible) {
    return null;
  }

  const twClassName = twMerge(
    clsx(
      `
        inset-x-0 top-0 flex w-full items-center justify-between gap-4 border-b
        border-border-variant-low bg-background-surface-container px-4 py-3
      `,
      className,
    ),
  );

  return (
    <div
      aria-label={mergedLabels.ariaLabel}
      className={twClassName}
      role="banner"
      {...properties}
    >
      <CloseButton
        aria-label={mergedLabels.dismissLabel}
        className="ms-0 w-5"
        onPress={dismissBanner}
        size="small"
        variant="white"
      />
      <div className="flex flex-1 items-center gap-3">
        {appIconUrl && (
          <div className="relative size-14">
            <NextImage
              alt=""
              className="object-contain"
              fill
              role="presentation"
              src={appIconUrl}
            />
          </div>
        )}

        <div className="flex flex-1 flex-col">
          <span
            className="
              text-s-body font-semibold text-foreground-primary-variant
            "
          >
            {mergedLabels.appName}
          </span>
          <span className="text-xs-heading text-foreground-on-surface">
            {mergedLabels.appDescription}
          </span>
        </div>

        <Button
          className="
            min-w-0 rounded-md bg-background-primary px-6 py-2 text-s-body
            font-extrabold text-foreground-on-primary transition-colors

            hover:bg-royal-blue/90

            focus:ring-2 focus:ring-royal-blue focus:ring-offset-2
            focus:outline-none
          "
          onPress={openApp}
        >
          {mergedLabels.ctaText}
        </Button>
      </div>
    </div>
  );
};
export { useSmartBanner } from "./use-smart-banner";
export { DEFAULT_LABELS, type SmartBannerLabels } from "./labels";
